import castArray from "lodash/castArray";
import every from "lodash/every";
import filter from "lodash/filter";
import get from "lodash/get";
import head from "lodash/head";
import isEmpty from "lodash/isEmpty";
import map from "lodash/map";
import xor from "lodash/xor";

import type { Question } from "@/repositories/qae/types";

// TODO: replace all the hardcoded strings corresponding to enums with the actual enums
// once the enums are available in the project.

/**
 * Evaluates whether the selected answers for a question are correct.
 *
 * @param question - The question object containing type and answers
 * @param selectedAnswerIds - Array of IDs representing the selected answers
 * @returns boolean - True if the answer(s) are correct, false otherwise
 *
 * For SINGLE type questions:
 * - Returns true if the selected answer is marked as correct
 * - Returns false if the selected answer is incorrect or not found
 *
 * For MULTIPLE type questions:
 * - Returns true if the selected answer IDs exactly match the correct answer IDs
 * - Returns false if there's any mismatch between selected and correct answers
 *
 * Returns false for any unhandled question types
 */
export const evaluateAnswer = (question: Question, selectedAnswerIds: string[]): boolean => {
  const questionType = get(question, "type") ?? "";
  const questionAnswers = get(question, "answers") ?? [];

  if (questionType === "SINGLE") {
    const selectedAnswerId = head(selectedAnswerIds);
    const answersWithSameId = filter(questionAnswers, { id: selectedAnswerId });
    return answersWithSameId.some((answer) => get(answer, "correct") ?? false);
  }
  if (questionType === "MULTIPLE") {
    const correctAnswerIds = map(filter(questionAnswers, "correct"), "id");
    return isEmpty(xor(correctAnswerIds, selectedAnswerIds));
  }
  return false;
};

/**
 * Gets correct answers from a question object using lodash for safety
 * @param question - The question object with answers property
 * @returns {Array} - Array of correct answers
 *
 * Example usage:
 * const correctAnswers = getCorrectAnswers(questionObject);
 */
export function getCorrectAnswers(question: Question) {
  const answers = get(question, "answers", []);

  const answersArray = isEmpty(answers) ? [] : castArray(answers);

  const correctAnswers = filter(answersArray, (answer) => get(answer, "correct", false) === true);

  return correctAnswers;
}

export function isOpenQuestion(question: Question) {
  const answers = get(question, "answers", []);
  // An open question might be represented as a question with no predefined answers,
  // or where all answers are considered "correct" by default (e.g. a survey question).
  // If there are no answers, or if every answer is marked as correct,
  // it can be considered an open-ended or non-scorable question.
  if (isEmpty(answers)) {
    return false; // Or true, based on product definition of open question with no answers.
  }
  return every(answers, (answer) => get(answer, "correct", false) === true);
}
